import { useState, useEffect, useCallback } from 'react';
import { projectsAPI, clientsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingProject, setDeletingProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    client_name: '',
    stakeholder_name: '',
    segment: '',
    status: 'ongoing',
    start_date: '',
    end_date: '',
    particulars: [],
    currency: 'INR',
  });

  const currencies = ['INR', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'SGD', 'AED'];
  const segments = ['Video', 'Design', 'Social Media', 'Branding', 'Website', 'Events', 'Gifting', 'AI Work', 'Creative Strategy', 'Others'];

  const fetchProjects = useCallback(async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, [fetchProjects, fetchClients]);

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        client_id: clientId,
        client_name: client.name,
        currency: client.default_currency || 'INR',
      });
    } else {
      setFormData({
        ...formData,
        client_id: '',
        client_name: '',
      });
    }
  };

  const handleParticularsChange = (index, field, value) => {
    const newParticulars = [...formData.particulars];
    newParticulars[index][field] = value;
    setFormData({ ...formData, particulars: newParticulars });
  };

  const addParticularsItem = () => {
    setFormData({
      ...formData,
      particulars: [...formData.particulars, { _rid: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, item_name: '', item_description: '', quantity: 1, rate: 0, segment: '' }],
    });
  };

  const removeParticularsItem = (index) => {
    if (formData.particulars.length > 0) {
      const newParticulars = formData.particulars.filter((_, i) => i !== index);
      setFormData({ ...formData, particulars: newParticulars });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        particulars: formData.particulars.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity) || 0,
          rate: parseFloat(item.rate) || 0,
        })),
      };
      
      if (editingProject) {
        await projectsAPI.update(editingProject.id, data);
        toast.success('Project updated successfully');
      } else {
        await projectsAPI.create(data);
        toast.success('Project created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Validation error').join(', '));
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || 'Operation failed');
      } else {
        toast.error(detail || 'Operation failed');
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await projectsAPI.delete(id);
      toast.success('Project deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingProject(null);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const openDeleteDialog = (project) => {
    setDeletingProject(project);
    setDeleteDialogOpen(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      client_id: project.client_id || '',
      client_name: project.client_name,
      stakeholder_name: project.stakeholder_name || '',
      segment: project.segment || '',
      status: project.status,
      start_date: project.start_date,
      end_date: project.end_date || '',
      particulars: (project.particulars || []).map((p, i) => ({
        ...p,
        _rid: p._rid || `row-${project.id}-${i}`,
      })),
      currency: project.currency || 'INR',
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProject(null);
    setFormData({
      name: '',
      client_id: '',
      client_name: '',
      stakeholder_name: '',
      segment: '',
      status: 'ongoing',
      start_date: '',
      end_date: '',
      particulars: [],
      currency: 'INR',
    });
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const calculateProjectTotal = (particulars) => {
    if (!particulars || particulars.length === 0) return 0;
    return particulars.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  };

  if (loading) {
    return <div className="text-center py-12">Loading projects...</div>;
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="projects-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Projects
          </h1>
          <p className="text-muted-foreground mt-2">Manage your project portfolio</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2" data-testid="create-project-button">
              <Plus className="h-4 w-4" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
            <DialogHeader>
              <DialogTitle>{editingProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
              <DialogDescription>
                {editingProject ? 'Update project details' : 'Add a new project to your portfolio'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 1. Client Name - Dropdown */}
              <div className="space-y-2">
                <Label htmlFor="client">Client Name *</Label>
                <select
                  id="client"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={formData.client_id}
                  onChange={(e) => handleClientChange(e.target.value)}
                  required
                  data-testid="project-client-select"
                >
                  <option value="">Select a client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Project Name - Text Box */}
              <div className="space-y-2">
                <Label htmlFor="project-name">Project Name *</Label>
                <Input
                  id="project-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="project-name-input"
                />
              </div>

              {/* 3. Stakeholder Name - Text Box */}
              <div className="space-y-2">
                <Label htmlFor="stakeholder-name">Stakeholder Name</Label>
                <Input
                  id="stakeholder-name"
                  value={formData.stakeholder_name}
                  onChange={(e) => setFormData({ ...formData, stakeholder_name: e.target.value })}
                  placeholder="Enter stakeholder name"
                  data-testid="project-stakeholder-input"
                />
              </div>

              {/* Segment - Dropdown */}
              <div className="space-y-2">
                <Label htmlFor="segment">Segment</Label>
                <select
                  id="segment"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={formData.segment}
                  onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                  data-testid="project-segment-select"
                >
                  <option value="">Select Segment</option>
                  {segments.map((seg) => (
                    <option key={seg} value={seg}>{seg}</option>
                  ))}
                </select>
              </div>

              {/* 4. Status - Dropdown (ongoing, completed) */}
              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <select
                  id="status"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  required
                  data-testid="project-status-select"
                >
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {/* 5 & 6. Start Date and End Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start-date">Start Date *</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    data-testid="project-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    data-testid="project-end-date-input"
                  />
                </div>
              </div>

              {/* Currency */}
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <select
                  id="currency"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  data-testid="project-currency-select"
                >
                  {currencies.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>

              {/* 7. Particulars - Table Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Particulars</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addParticularsItem} data-testid="add-particular-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>
                
                {formData.particulars.length > 0 && (
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2 text-sm font-medium">Item Name</th>
                          <th className="text-left p-2 text-sm font-medium">Description</th>
                          <th className="text-left p-2 text-sm font-medium w-32">Segment</th>
                          <th className="text-right p-2 text-sm font-medium w-20">Qty</th>
                          <th className="text-right p-2 text-sm font-medium w-28">Rate</th>
                          <th className="text-right p-2 text-sm font-medium w-28">Amount</th>
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.particulars.map((item, index) => (
                          <tr key={item._rid || `row-${index}`} className="border-t">
                            <td className="p-2">
                              <Input
                                value={item.item_name}
                                onChange={(e) => handleParticularsChange(index, 'item_name', e.target.value)}
                                placeholder="Item name"
                                className="h-8"
                                data-testid={`particular-name-${index}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                value={item.item_description || ''}
                                onChange={(e) => handleParticularsChange(index, 'item_description', e.target.value)}
                                placeholder="Description"
                                className="h-8"
                                data-testid={`particular-desc-${index}`}
                              />
                            </td>
                            <td className="p-2">
                              <select
                                value={item.segment || ''}
                                onChange={(e) => handleParticularsChange(index, 'segment', e.target.value)}
                                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                                data-testid={`particular-segment-${index}`}
                              >
                                <option value="">Select</option>
                                {segments.map((seg) => (
                                  <option key={seg} value={seg}>{seg}</option>
                                ))}
                              </select>
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleParticularsChange(index, 'quantity', e.target.value)}
                                className="h-8 text-right"
                                min="0"
                                data-testid={`particular-qty-${index}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                value={item.rate}
                                onChange={(e) => handleParticularsChange(index, 'rate', e.target.value)}
                                className="h-8 text-right"
                                min="0"
                                step="0.01"
                                data-testid={`particular-rate-${index}`}
                              />
                            </td>
                            <td className="p-2 text-right font-medium">
                              {formData.currency} {((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)).toLocaleString()}
                            </td>
                            <td className="p-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeParticularsItem(index)}
                                className="h-8 w-8 p-0"
                                data-testid={`remove-particular-${index}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {formData.particulars.length > 0 && (
                        <tfoot className="bg-muted/50">
                          <tr>
                            <td colSpan="4" className="p-2 text-right font-semibold">Total:</td>
                            <td className="p-2 text-right font-bold">
                              {formData.currency} {calculateProjectTotal(formData.particulars).toLocaleString()}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
                
                {formData.particulars.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4 border rounded-md">
                    No items added. Click "Add Item" to add line items.
                  </p>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="project-submit-button">
                  {editingProject ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {projects.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">
              No projects yet. Create your first project to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {projects.map((project) => (
            <Card key={project.id} className="hover:shadow-md transition-shadow" data-testid={`project-card-${project.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-xl font-semibold">{project.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{project.client_name}</p>
                    {project.stakeholder_name && (
                      <p className="text-xs text-muted-foreground">Stakeholder: {project.stakeholder_name}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(project)}
                      data-testid={`edit-project-${project.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDeleteDialog(project)}
                      data-testid={`delete-project-${project.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Status</p>
                    <span
                      className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${
                        project.status === 'ongoing'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {project.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Start Date</p>
                    <p className="text-sm font-medium mt-1">{project.start_date}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">End Date</p>
                    <p className="text-sm font-medium mt-1">{project.end_date || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Items</p>
                    <p className="text-sm font-medium mt-1">{project.particulars?.length || 0} items</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Total Value</p>
                    <p className="text-sm font-medium mt-1">
                      {project.currency || 'INR'} {calculateProjectTotal(project.particulars || []).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingProject?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletingProject(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deletingProject?.id)}
              data-testid="confirm-delete-button"
            >
              Delete Project
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
